import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BaseUrl } from '../App';
import { Circles } from 'react-loader-spinner';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar as fullStar, faStarHalfAlt, faStar as emptyStar } from "@fortawesome/free-solid-svg-icons";
import { FadeLoader } from 'react-spinners';


const ServiceBookings = () => {
  const [activeTab, setActiveTab] = useState('today');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAddress, setSelectedAddress] = useState("");
const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [entriesPerPage] = useState(10);
  const [todayBookings, setTodayBookings] = useState([]);
  const [futureBookings, setFutureBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError("");
      }, 5000); // 5 seconds
    }
    return () => clearTimeout(timer);
  }, [error]);
  // Dynamically fetch userId on component mount
  useEffect(() => {
    const fetchedUserId = sessionStorage.getItem('userId');
    if (fetchedUserId) {
      setUserId(fetchedUserId);
      fetchTodayBookings(fetchedUserId); // Fetch today's bookings by default
    }
  }, []);

  // Function to fetch today's bookings dynamically using the API
  const fetchTodayBookings = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/today.booking.php`, {
        action: "get_today_bookings",
        api_key: "1", // API key, replace as necessary
        staff_id: userId, // Dynamic userId
      });

      if (response.data.status === 'true') {
        setTodayBookings(response.data.response); // Set the data to todayBookings state
      } else {
        setTodayBookings([]);
      }
    } catch (error) {
      console.error('Error fetching today bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFutureBookings = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/upcoming_appointment.php`, {
        action: "get_all_upcoming_appointment",
        api_key: "1",
        user_id: userId,
        type: "staff",
      });

      if (response.data.status === 'true') {
        setFutureBookings(response.data.response);
      } else {
        setFutureBookings([]);
      }
    } catch (error) {
      console.error('Error fetching future bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPastBookings = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/past_appointment.php`, {
        action: "get_all_past_appointment",
        api_key: "1",
        user_id: userId,
        type: "staff",
      });

      if (response.data.status === 'true') {
        setPastBookings(response.data.response);
      } else {
        setPastBookings([]);
      }
    } catch (error) {
      console.error('Error fetching past bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setIsDropdownOpen(false);
    
    if (tab === 'today' && userId) {
      fetchTodayBookings(userId);
    }
    if (tab === 'future' && userId) {
      fetchFutureBookings(userId);
    }
    if (tab === 'past' && userId) {
      fetchPastBookings(userId);
    }
  };

  const handleSort = (key) => {
    const normalizedKey = key.toLowerCase().replace(/\s+/g, '_');
    setSortConfig((prev) => ({
      key: normalizedKey,
      direction: prev.key === normalizedKey && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (column) => {
    if (sortConfig.key !== column) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Sorting functions for different booking types
  const sortedTodayBookings = [...todayBookings].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const keyMap = {
      'service': 'title', 'date': 'appointment_date',
      'customer_name': 'customer_name', 'email': 'customer_email',
      'address': 'address', 'phone': 'phone', 'net_total': 'total_payment',
      'staff_booking': 'staff_names', 'payment_status': 'booking_status',
    };
    const mappedKey = keyMap[sortConfig.key] || sortConfig.key;
    if (a[mappedKey] < b[mappedKey]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[mappedKey] > b[mappedKey]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const sortedFutureBookings = [...futureBookings].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const keyMap = {
      'service': 'title', 'date': 'appointment_date',
      'customer_name': 'customer_name', 'email': 'customer_email',
      'address': 'address', 'phone': 'phone', 'net_total': 'total_payment',
      'staff_booking': 'staff_names', 'payment_status': 'booking_status',
    };
    const mappedKey = keyMap[sortConfig.key] || sortConfig.key;
    if (a[mappedKey] < b[mappedKey]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[mappedKey] > b[mappedKey]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const sortedPastBookings = [...pastBookings].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const keyMap = {
      'service': 'title', 'date': 'appointment_date',
      'customer_name': 'customer_name', 'email': 'customer_email',
      'address': 'address', 'phone': 'phone', 'net_total': 'total_payment',
      'staff_booking': 'staff_names', 'payment_status': 'booking_status',
    };
    const mappedKey = keyMap[sortConfig.key] || sortConfig.key;
    if (a[mappedKey] < b[mappedKey]) return sortConfig.direction === 'asc' ? -1 : 1;
    if (a[mappedKey] > b[mappedKey]) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalTodayPages = Math.ceil(sortedTodayBookings.length / entriesPerPage);
  const paginatedTodayData = sortedTodayBookings.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const totalFuturePages = Math.ceil(sortedFutureBookings.length / entriesPerPage);
  const paginatedFutureData = sortedFutureBookings.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const totalPastPages = Math.ceil(sortedPastBookings.length / entriesPerPage);
  const paginatedPastData = sortedPastBookings.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage
  );

  const handleAction = async (action, orderId, id) => {
    setLoading(true);
    if (action === "accept") {
      try {
        const response = await axios.post(`${BaseUrl}/backend/api/accept.appointment.php`, {
          action: "accept_appointment_staff",
          api_key: "1", // Use the correct API key if needed
          order_id: orderId, // The dynamic order_id
          staff_status: "C", // Accept status
          idd: id, // The dynamic staff status ID
        });
        if (response.data.status === 'true') {
          setSuccessMessage("Appointment Accept successfully!");
          alert("Appointment Accepted Successfully!");
          fetchTodayBookings(userId); // Or call the relevant fetch function to update the table
          fetchFutureBookings(userId);
          fetchPastBookings(userId);
        } else {
          alert("Failed to accept the appointment!");
        }
      } catch (error) {
        console.error("Error accepting appointment:", error);
      }
      finally {
        setLoading(false);
      }
    }
  
    if (action === "decline") {
      try {
        const response = await axios.post(`${BaseUrl}/backend/api/accept.appointment.php`, {
          action: "decline_appointment_staff",
          api_key: "1", // API key, replace as necessary
          order_id: orderId, // The dynamic order_id
          staff_status: "CS", // Declined status (CS means "Cancelled" or "Declined")
          idd: id, // The dynamic staff status ID
        });
  
        if (response.data.status === 'true') {
          setSuccessMessage("Appointment Declined successfully!");
          alert("Appointment Declined Successfully!");
          fetchTodayBookings(userId); // Re-fetch the relevant bookings to update the table
          fetchFutureBookings(userId);
          fetchPastBookings(userId);
        } else {
          alert("Failed to decline the appointment!");
        }
      } catch (error) {
        console.error("Error declining appointment:", error);
      }
      finally {
        setLoading(false);
      }
    }
  };
  
  const updatePaymentStatus = async (orderId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BaseUrl}/backend/api/accept.appointment.php`, {
        action: 'update_payment_status',
        api_key: '1',
        order_id: orderId, // dynamic order_id passed here
        payment_status: 'Complete', // Payment status is set to "Complete"
      });

      if (response.data.status === 'true') {
        setSuccessMessage("Payment status updated to 'Complete'");
        alert("Payment status updated to 'Complete'");
        // Re-fetch the bookings to reflect the updated payment status
        fetchTodayBookings(userId);
        fetchFutureBookings(userId);
        fetchPastBookings(userId);
      } else {
        alert('Failed to update payment status');
      }
    } catch (error) {
      console.error('Error updating payment status:', error);
    }
    finally {
      setLoading(false);
    }
  };

  // Helper function to render rating stars

  const renderRatingStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= Math.floor(rating);
          const isHalfFilled = star === Math.floor(rating) + 1 && rating % 1 !== 0;
          return (
            <span key={star} className="text-lg text-yellow-500">
              {isFilled ? (
                <FontAwesomeIcon icon={fullStar} />
              ) : isHalfFilled ? (
                <FontAwesomeIcon icon={faStarHalfAlt} />
              ) : (
                <FontAwesomeIcon icon={emptyStar} className="text-gray-300" />
              )}
            </span>
          );
        })}
      </div>
    );
  };
  // Determine current data to display based on active tab
  const currentData = 
    activeTab === "today" 
      ? paginatedTodayData 
      : activeTab === "future" 
        ? paginatedFutureData 
        : paginatedPastData;
  
  const totalPages = 
    activeTab === "today" 
      ? totalTodayPages 
      : activeTab === "future" 
        ? totalFuturePages 
        : totalPastPages;
  
  return (
    <div className="relative min-h-screen">
    {/* Loading Indicator */}
      {loading && (
           <div className="fixed inset-0 flex items-center justify-center z-50">
             <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
               <FadeLoader 
                 height={15}
                 width={5}
                 color="#ffffff"
                 ariaLabel="loading"
               />
               <h1 className="mt-1 text-lg text-white">Please wait...</h1>
             </div>
           </div>
         )}
         

    <div className="bg-gray-50 min-h-screen">
      {/* Container for the entire component */}
      <div className="container mx-auto px-4 py-6">
        {/* Alert Messages */}
        {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
          <span className="block sm:inline">{error}</span>
        </div>
      )}
        {/* Tab Navigation - Dropdown for mobile, Tabs for larger screens */}
        <div className="mb-6">
          <div className="block md:hidden relative">
            <button 
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none"
            >
              <span className="capitalize">{activeTab} Bookings</span>
              <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-1">
                {["today", "future", "past"].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      activeTab === tab ? "bg-blue-100 text-blue-900" : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)} Bookings
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Desktop Tabs */}
          <div className="hidden md:flex border-b border-gray-300">
            {["today", "future", "past"].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`px-6 py-3 text-sm font-medium border-b-2 ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)} Bookings
              </button>
            ))}
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 capitalize">
          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Bookings
        </h2>

        {/* Loading indicator */}
        {/* {loading && (
          <div className="flex justify-center items-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )} */}

        {/* Card view for small screens and table for larger screens */}
        {!loading && (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {currentData.length > 0 ? (
                currentData.map((booking, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <div className="flex justify-between mb-2">
                      <h3 className="font-bold text-blue-700">{booking.title}</h3>
                      <span className="text-sm bg-blue-100 text-blue-800 rounded-full px-3 py-1">
                        {new Date(booking.appointment_date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Customer</span>
                        <span className="font-medium">{booking.client_name}</span>
                      </div>
                      
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Contact</span>
                        <span>{booking.phone}</span>
                        <span className="text-sm text-gray-600">{booking.user_email}</span>
                      </div>
                      
                      <div className="flex flex-col">
                        <span className="text-xs text-gray-500">Address</span>
                        <span className="text-sm">{booking.address || "null"}</span>
                        {/* <td className="px-4 py-2">
  {booking?.address?.length > 10 ? (
    <div className="relative group">
      <div>{booking?.address.substring(0, 10)}... 
        <button 
          className="text-blue-500 hover:text-blue-700 text-xs ml-1"
          onClick={() => {
            // Set state to show popup with this address
            setSelectedAddress(booking?.address);
            setShowAddressPopup(true);
          }}
        >
          More
        </button>
      </div>
    </div>
  ) : (
    booking?.address
  )}
</td> */}
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500">Payment</span>
                          <span className="font-bold">${parseFloat(booking.total_payment).toFixed(2)}</span>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-xs text-gray-500">Status</span>
                          <span className="font-semibold">{booking.booking_status}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Rating & Review */}
                    <div className="mb-4">
                      <span className="text-xs text-gray-500 block mb-1">Rating & Review</span>
                      {renderRatingStars(booking.rating)}
                      <p className="text-sm mt-1 text-gray-600">{booking.review || "No Review"}</p>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col space-y-2">
                      {/* Accept/Decline Buttons */}
                      {(booking.booking_status === "A" || booking.booking_status === "RS") && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAction("accept", booking.order_id, booking.id)}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleAction("decline", booking.order_id, booking.id)}
                            className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                          >
                            Decline
                          </button>
                        </div>
                      )}
                      
                      {booking.booking_status === "C" && (
                        <button className="w-full px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm" disabled>
                          Accepted
                        </button>
                      )}
                      
                      {booking.booking_status === "CS" && (
                        <button className="w-full px-3 py-2 bg-red-100 text-red-800 rounded-lg text-sm" disabled>
                          Declined
                        </button>
                      )}
                      
                      {/* Payment Button */}
                      {booking.payment_status === "Complete" ? (
                        <button className="w-full px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm" disabled>
                          Payment Completed
                        </button>
                      ) : (
                        <button
                          onClick={() => updatePaymentStatus(booking.order_id)}
                          className="w-full px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                        >
                          Mark as Paid
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
                  No bookings found
                </div>
              )}
            </div>
            
            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full bg-white border border-gray-200 shadow-md rounded-lg">
                <thead className="bg-blue-100">
                  <tr>
                    {[
                      "Service",
                      "Date",
                      "Customer Name",
                      "Email",
                      "Address",
                      "Phone",
                      "Net Total",
                      "Booking Status",
                      "Action",
                      "Payment Status",
                      "Rating & Review",
                    ].map((header, index) => (
                      <th
                        key={index}
                        className="px-4 py-2 border-b text-left cursor-pointer hover:bg-blue-200"
                        onClick={() => handleSort(header.toLowerCase().replace(/\s+/g, "_"))}
                      >
                        <span className="inline">{header}</span>
                        <span className="ml-1 text-gray-500">
                          {getSortIndicator(header.toLowerCase().replace(/\s+/g, "_"))}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((booking, index) => (
                      <tr key={index} className="hover:bg-gray-50 border-b">
                        <td className="px-4 py-2">{booking.title}</td>
                        <td className="px-4 py-2">
                          {new Date(booking.appointment_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-2">{booking.client_name}</td>
                        <td className="px-4 py-2">{booking.user_email}</td>
                        {/* <td className="px-4 py-2">{booking.address}</td> */}
  <td className="px-4 py-2">
  {booking?.address ? (
    booking.address.length > 10 ? (
      <div className="relative group">
        <div>{booking.address.substring(0, 10)}... 
          <button
            className="text-blue-500 hover:text-blue-700 text-xs ml-1"
            onClick={() => {
              // Set state to show popup with this address
              setSelectedAddress(booking.address);
              setShowAddressPopup(true);
            }}
          >
            More
          </button>
        </div>
      </div>
    ) : (
      booking.address
    )
  ) : (
    "N/A"
  )}
</td>
                        <td className="px-4 py-2">{booking.phone}</td>
                        <td className="px-4 py-2">
                          ${parseFloat(booking.total_payment).toFixed(2)}
                        </td>
                        <td className="px-4 py-2">{booking.booking_status}</td>
                        <td className="px-4 py-2">
                          {/* Conditionally render Accept and Decline buttons side by side */}
                          {(booking.booking_status === "A" || booking.booking_status === "RS") && (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleAction("accept", booking.order_id, booking.id)}
                                className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => handleAction("decline", booking.order_id, booking.id)}
                                className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                              >
                                Decline
                              </button>
                            </div>
                          )}
                          {booking.booking_status === "C" && (
                            <button className="px-3 py-1 bg-green-100 text-green-800 rounded-lg text-sm" disabled>
                              Accepted
                            </button>
                          )}
                          {booking.booking_status === "CS" && (
                            <button className="px-3 py-1 bg-red-100 text-red-800 rounded-lg text-sm" disabled>
                              Declined
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {booking.payment_status === "Complete" ? (
                            <button className="px-3 py-1 bg-blue-700 text-white rounded-lg text-sm" disabled>
                              Completed
                            </button>
                          ) : (
                            <button
                              onClick={() => updatePaymentStatus(booking.order_id)}
                              className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600"
                            >
                              Paid
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {renderRatingStars(booking.rating)}
                          <p className="text-sm text-gray-600 mt-1">
                            {booking.review || "No Review"}
                          </p>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center py-6 text-gray-500">
                        No bookings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Address Popup */}
{showAddressPopup && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-4 rounded-lg shadow-lg max-w-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Complete Address</h3>
        <button 
          onClick={() => setShowAddressPopup(false)} 
          className="text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>
      <p className="text-gray-700">{selectedAddress}</p>
      <div className="mt-4 flex justify-end">
        <button 
          onClick={() => setShowAddressPopup(false)} 
          className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

        {/* Pagination - same for both views */}
        {!loading && currentData.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg w-full sm:w-auto ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-700"
              }`}
            >
              Previous
            </button>
            <span className="text-gray-700">
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`px-4 py-2 rounded-lg w-full sm:w-auto ${
                currentPage === totalPages || totalPages === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-700"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
      
  </div>
  );
};

export default ServiceBookings;

